/**
 * Flex Bundle Builder Web Component
 *
 * A customizable bundle builder that supports:
 * - Mix and Match bundles (choose up to X items)
 * - Fixed price and percentage discount pricing
 * - Flex Bundles app cart integration
 */

class FlexBundleBuilder extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.sectionId = this.dataset.sectionId;
    this.bundleType = this.dataset.bundleType || 'mix_and_match';
    this.pricingType = this.dataset.pricingType || 'fixed_price';
    this.minItems = parseInt(this.dataset.minItems) || 3;
    this.maxItems = parseInt(this.dataset.maxItems) || 6;
    this.parentVariantId = this.dataset.parentVariantId;

    // Parent bundle product's catalog price in presentment-currency cents,
    // rendered server-side so it reflects the active market's catalog price
    // (e.g. £50 GB / €67 FR) rather than a currency-converted base price.
    this.bundlePrice = parseInt(this.dataset.bundlePrice) || 0;
    // Market-localized bundle title shown in cart/checkout via _settings.
    this.cartTitle = this.dataset.cartTitle || '';

    this.bundleTitle = this.dataset.bundleTitle || 'Bundle';
    this.bundleImage = this.dataset.bundleImage || '';
    this.savingsLabel = this.dataset.savingsLabel || 'YOU SAVED';
    this.addToCartText = this.dataset.addToCartText || 'Add Kit to Bag';
    this.collectionHandle = this.dataset.collectionHandle;
    this.productsPerPage = parseInt(this.dataset.productsPerPage) || 32;
    this.currencyCode = this.dataset.currencyCode || 'USD';
    this.locale = this.dataset.locale || 'en-US';
    // Exchange rate from the shop's base currency to the active presentment
    // currency (1 when not converting). Fixed-price tier values are authored in
    // shop-currency cents, so they must be multiplied by this rate to match the
    // already-localized per-item prices. Without it, a $75 USD fixed bundle
    // checks out as 75 of the local currency (e.g. ~$4 USD in MXN). See getFixedTierPrice.
    this.currencyRate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
    try {
      this.currencyFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currencyCode,
      });
    } catch (e) {
      this.currencyFormatter = null;
    }

    // Parse tiers configuration and sort by size ascending
    this.tiers = [];
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
      this.tiers.sort((a, b) => a.size - b.size);
    } catch (e) {
      console.error('Error parsing tiers:', e);
    }

    // Parse steps configuration (for preset bundles)
    this.steps = [];
    try {
      this.steps = JSON.parse(this.dataset.steps || '[]');
    } catch (e) {
      console.error('Error parsing steps:', e);
    }

    // State
    this.selectedProducts = {}; // { slotIndex: productData }
    this.currentStep = 0; // For preset bundles
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;

    // DOM References (set in connectedCallback)
    this.slots = null;
    this.productGrid = null;
    this.productGrids = null; // Multiple grids for preset bundles
    this.savingsDisplay = null;
    this.pagination = null;
    this.quickshopModal = null;
    this.reviewPanel = null;
    this.addToCartBtn = null;
    this.stepTabs = null;
    this.stepPanels = null; // Step content panels for preset bundles
    this.setBtns = null;
    this.stepInstruction = null;
    this.stepNameEl = null;

    // Progress bar references
    this.progressBar = null;
    this.progressFill = null;
    this.progressMessage = null;

    // Kit drawer references
    this.kitDrawer = null;
    this.kitDrawerItems = null;
    this.viewKitBtn = null;
    this.drawerSavingsAmount = null;
    this.drawerTotalDisplay = null;
    this.drawerTotal = null;
    this.drawerAddToCartBtn = null;

    // Selected tier for preset bundles
    this.selectedTierIndex = 0;

    // Quickshop state
    this.quickshopProduct = null;
    this.quickshopSelectedVariant = null;

    // Quick-add modal state (theme modal)
    this.pendingModalCard = null;

    // Bind methods
    this.handleAddToKit = this.handleAddToKit.bind(this);
    this.handleQuickshopAdd = this.handleQuickshopAdd.bind(this);
    this.handleSlotClick = this.handleSlotClick.bind(this);
    this.addBundleToCart = this.addBundleToCart.bind(this);
  }

  connectedCallback() {
    // Get DOM references
    this.slots = this.querySelectorAll('[js-bundle-slot]');
    this.productGrid = this.querySelector('[js-product-grid]');
    this.productGrids = this.querySelectorAll('[js-product-grid]'); // All grids for preset
    this.savingsDisplay = this.querySelector('[js-savings-amount]');
    this.pagination = this.querySelector('[js-pagination]');
    this.quickshopModal = this.querySelector('[js-quickshop-modal]');
    this.reviewPanel = this.querySelector('[js-bundle-review]');
    this.addToCartBtn = this.querySelector('[js-add-to-cart]');
    this.stepTabs = this.querySelectorAll('[js-step-tab]');
    this.stepPanels = this.querySelectorAll('[js-step-panel]'); // Step content panels
    this.setBtns = this.querySelectorAll('[js-set-btn]');
    this.stepInstruction = this.querySelector('[js-step-instruction]');
    this.stepNameEl = this.querySelector('[js-step-name]');

    // Progress bar references
    this.progressBar = this.querySelector('[js-bundle-progress]');
    this.progressFill = this.querySelector('[js-progress-fill]');
    this.progressMessage = this.querySelector('[js-progress-message]');

    // Kit drawer references
    this.kitDrawer = this.querySelector('[js-kit-drawer]');
    this.kitDrawerItems = this.querySelector('[js-kit-drawer-items]');
    this.viewKitBtn = this.querySelector('[js-view-kit-btn]');
    this.drawerSavingsAmount = this.querySelector('[js-drawer-savings-amount]');
    this.drawerTotalDisplay = this.querySelector('[js-drawer-total-display]');
    this.drawerTotal = this.querySelector('[js-drawer-total]');
    this.drawerAddToCartBtn = this.querySelector('[js-drawer-add-to-cart]');

    // Initialize total pages from server-rendered pagination
    if (this.pagination) {
      this.totalPages = parseInt(this.pagination.dataset.totalPages) || 1;
    }

    // Initialize
    this.initEventListeners();
    this.initCardSwipers();
    this.updateUI();
    this.initStickyProgressTop();

  }

  initStickyProgressTop() {
    if (!this.progressBar) return;

    const updateTop = () => {
      const announcementBar = document.querySelector('.sticky-announcement-bar');
      if (announcementBar) {
        const barRect = announcementBar.getBoundingClientRect();
        const barBottom = barRect.bottom > 0 ? barRect.bottom : 0;
        this.progressBar.style.top = barBottom + 'px';
      }
    };

    updateTop();
    window.addEventListener('resize', updateTop);
    window.addEventListener('scroll', updateTop);

    // Watch for announcement bar being dismissed
    const observer = new MutationObserver(updateTop);
    const announcementContainer = document.getElementById('_loomi_addon_1762803764783');
    if (announcementContainer) {
      observer.observe(announcementContainer, { attributes: true, childList: true, subtree: true });
    }
  }

  initEventListeners() {
    // Product grid clicks (event delegation)
    // For preset bundles, attach to all grids; for others, just the main grid
    const gridsToWatch = this.bundleType === 'preset' && this.productGrids.length > 0
      ? this.productGrids
      : this.productGrid ? [this.productGrid] : [];

    gridsToWatch.forEach(grid => {
      grid.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[js-add-to-kit]');
        const quickshopBtn = e.target.closest('[js-open-quickshop]');
        const swiperPrev = e.target.closest('[js-swiper-prev]');
        const swiperNext = e.target.closest('[js-swiper-next]');

        if (addBtn) {
          e.preventDefault();
          const card = addBtn.closest('[js-product-card]');
          this.trackProductCardClick(addBtn, card);
          this.handleAddToKit(card);
        }

        if (quickshopBtn) {
          e.preventDefault();
          const card = quickshopBtn.closest('[js-product-card]');
          this.trackProductCardClick(quickshopBtn, card);
          this.openQuickshop(card);
        }

        if (swiperPrev) {
          e.preventDefault();
          this.handleSwiperNav(swiperPrev.closest('[js-card-swiper]'), 'prev');
        }

        if (swiperNext) {
          e.preventDefault();
          this.handleSwiperNav(swiperNext.closest('[js-card-swiper]'), 'next');
        }

        // Image gallery click → trigger the card's CTA (ADD TO KIT / SELECT SHADE)
        // Skip clicks on swiper navigation, pagination, and other interactive controls
        const mediaGallery = e.target.closest('.card__media');
        if (
          mediaGallery &&
          !e.target.closest('.swiper-button-prev, .swiper-button-next, .swiper-pagination, button, a, modal-opener')
        ) {
          const card = mediaGallery.closest('[js-product-card]');
          if (card) {
            const cta = card.querySelector('[js-add-to-kit]') || card.querySelector('modal-opener button');
            if (cta && !cta.disabled) cta.click();
          }
        }
      });
    });

    // Slot clicks
    this.slots.forEach((slot, index) => {
      slot.addEventListener('click', () => this.handleSlotClick(index));

      // Remove button
      const removeBtn = slot.querySelector('[js-slot-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeProductFromSlot(index);
        });
      }

      // Remove label
      const removeLabel = slot.querySelector('[js-slot-remove-label]');
      if (removeLabel) {
        removeLabel.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeProductFromSlot(index);
        });
      }
    });

    // Quickshop modal
    if (this.quickshopModal) {
      // Close buttons
      this.quickshopModal.querySelectorAll('[js-quickshop-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeQuickshop());
      });

      // Add to kit button
      const quickshopAddBtn = this.quickshopModal.querySelector('[js-quickshop-add]');
      if (quickshopAddBtn) {
        quickshopAddBtn.addEventListener('click', () => this.handleQuickshopAdd());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.quickshopModal.classList.contains('is-open')) {
          this.closeQuickshop();
        }
      });
    }

    // Add to cart button
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => {
        this.trackBundleBuilderClick({
          clickType: 'CTA',
          productIndex: 'Review Your Kit',
          linkText: this.getClickText(this.addToCartBtn)
        });
        this.addBundleToCart();
      });
    }

    // Step tabs (preset bundles)
    if (this.stepTabs.length > 0) {
      this.stepTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const stepIndex = parseInt(tab.dataset.step);
          this.trackStepTabClick(stepIndex, tab);
          this.goToStep(stepIndex);
        });
      });
    }

    // Set size buttons (preset bundles)
    if (this.setBtns.length > 0) {
      this.setBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const tierIndex = parseInt(btn.dataset.tierIndex);
          this.trackBundleBuilderClick({
            clickType: 'CTA',
            linkText: this.getClickText(btn)
          });
          this.selectTier(tierIndex);
        });
      });
    }

    // Pagination
    if (this.pagination) {
      this.pagination.addEventListener('click', (e) => {
        const pageBtn = e.target.closest('[data-page]');
        if (pageBtn) {
          const page = parseInt(pageBtn.dataset.page);
          if (page && page !== this.currentPage) {
            this.goToPage(page);
          }
        }
      });
    }

    // View Kit Button
    if (this.viewKitBtn) {
      this.viewKitBtn.addEventListener('click', () => {
        this.trackBundleBuilderClick({
          clickType: 'CTA',
          productIndex: 'Review Your Kit',
          linkText: this.getClickText(this.viewKitBtn)
        });
        this.openKitDrawer();
      });
    }

    // Quick-add modals (theme native)
    this.initQuickAddModals();

    // Kit Drawer
    if (this.kitDrawer) {
      // Close buttons
      this.kitDrawer.querySelectorAll('[js-kit-drawer-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeKitDrawer());
      });

      // Drawer add to cart button
      if (this.drawerAddToCartBtn) {
        this.drawerAddToCartBtn.addEventListener('click', () => {
          this.trackBundleBuilderClick({
            clickType: 'CTA',
            productIndex: 'Review Your Kit',
            linkText: this.getClickText(this.drawerAddToCartBtn)
          });
          this.closeKitDrawer();
          this.addBundleToCart();
        });
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.kitDrawer.classList.contains('is-open')) {
          this.closeKitDrawer();
        }
      });
    }
  }

  initQuickAddModals() {
    // Track which card opened the modal
    // (guard against duplicate bindings when grids are re-initialized after AJAX loads)
    this.querySelectorAll('modal-opener').forEach(opener => {
      if (opener.dataset.bundleListenerBound) return;
      opener.dataset.bundleListenerBound = 'true';
      opener.addEventListener('click', () => {
        const card = opener.closest('[js-product-card]');
        if (card) {
          this.pendingModalCard = card;
          this.trackProductCardClick(opener.querySelector('button'), card);
        }
      });
    });

    // Catch clicks on the "ADD TO KIT" button anywhere on the page
    // (register the document listener only once per builder instance)
    if (this.modalKitBtnListenerBound) return;
    this.modalKitBtnListenerBound = true;
    document.addEventListener('click', (e) => {
      const kitBtn = e.target.closest('.flex-bundle__modal-kit-btn');
      if (!kitBtn || !this.pendingModalCard) return;

      e.preventDefault();

      // this.trackProductCardClick(kitBtn, this.pendingModalCard);

      const card = this.pendingModalCard;
      const modal = kitBtn.closest('quick-add-modal');

      // Read variant ID from the form inside the modal
      const form = kitBtn.closest('form') || (modal && modal.querySelector('form[data-type="add-to-cart-form"]'));
      const variantInput = form
        ? form.querySelector('input[name="id"]')
        : modal && modal.querySelector('input[name="id"]');

      if (!variantInput) return;

      const variantId = variantInput.value;

      // Look up variant in the card's data-variants-json
      let variants = [];
      try {
        variants = JSON.parse(card.dataset.variantsJson || '[]');
      } catch (e) {
        console.error('Error parsing variants:', e);
      }

      const variant = variants.find(v => String(v.id) === String(variantId));
      if (!variant) return;

      // Determine target slot: current step for preset, next available otherwise
      let slotIndex;
      if (this.bundleType === 'preset') {
        slotIndex = this.currentStep;
        if (this.selectedProducts[slotIndex]) {
          this.removeProductFromSlot(slotIndex);
        }
      } else {
        slotIndex = this.getNextAvailableSlotIndex();
        if (slotIndex === -1) {
          console.log('Bundle is full');
          if (modal) modal.hide();
          return;
        }
      }

      // Build product data
      const productData = {
        id: card.dataset.productId,
        handle: card.dataset.productHandle,
        variantId: String(variant.id),
        variantTitle: variant.title || 'Default Title',
        title: card.dataset.productTitle,
        price: variant.price,
        comparePrice: variant.compare_at_price || variant.price,
        valueMetafield: parseInt(card.dataset.valueMetafield) || 0,
        image: variant.featured_image ? variant.featured_image.src : card.dataset.productImage,
        swatchImage: variant.swatch_image || '',
        swatchColor: variant.swatch_color || '',
        cardElement: card
      };

      // Limit max 3 of the same variant
      if (this.getVariantCount(productData.variantId) >= 3) {
        alert('You can only add up to 3 of the same item.');
        if (modal) modal.hide();
        return;
      }

      this.addProductToSlot(slotIndex, productData);

      // Mark card as selected
      card.classList.add('is-selected');

      // Close the modal
      if (modal) modal.hide();

      // For preset bundles, auto-advance to next step
      if (this.bundleType === 'preset') {
        this.goToNextStep();
      }

      // Scroll to top for mix & match so user sees updated slots
      if (this.bundleType === 'mix_and_match') {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      this.pendingModalCard = null;
    });
  }

  initCardSwipers() {
    // Initialize simple swiper functionality for each card across all grids
    const swipers = this.querySelectorAll('[js-card-swiper]');
    swipers.forEach(swiper => {
      swiper.currentIndex = 0;
      const slides = swiper.querySelectorAll('.flex-bundle__card-swiper-slide');
      swiper.totalSlides = slides.length;
      // Ensure first slide is visible
      slides.forEach((slide, i) => {
        slide.style.display = i === 0 ? 'block' : 'none';
        slide.classList.toggle('is-active', i === 0);
      });
    });
  }

  handleSwiperNav(swiper, direction) {
    if (!swiper) return;

    const slides = swiper.querySelectorAll('.flex-bundle__card-swiper-slide');
    const prevBtn = swiper.querySelector('[js-swiper-prev]');
    const nextBtn = swiper.querySelector('[js-swiper-next]');

    if (direction === 'prev' && swiper.currentIndex > 0) {
      swiper.currentIndex--;
    } else if (direction === 'next' && swiper.currentIndex < slides.length - 1) {
      swiper.currentIndex++;
    }

    // Update slides visibility
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === swiper.currentIndex);
      slide.style.display = i === swiper.currentIndex ? 'block' : 'none';
    });

    // Update button states
    if (prevBtn) prevBtn.disabled = swiper.currentIndex === 0;
    if (nextBtn) nextBtn.disabled = swiper.currentIndex === slides.length - 1;
  }

  // ==================== Product Selection ====================

  handleAddToKit(card) {
    if (!card) return;

    let slotIndex;

    if (this.bundleType === 'preset') {
      // For preset bundles, add to the current step's slot
      slotIndex = this.currentStep;

      // Check if this slot is already filled
      if (this.selectedProducts[slotIndex]) {
        // Replace the existing product
        this.removeProductFromSlot(slotIndex);
      }
    } else {
      // For mix and match / custom, find next available slot
      slotIndex = this.getNextAvailableSlotIndex();
      if (slotIndex === -1) {
        console.log('Bundle is full');
        return;
      }
    }

    const productData = this.extractProductData(card);

    // Limit max 3 of the same variant
    if (this.getVariantCount(productData.variantId) >= 3) {
      alert('You can only add up to 3 of the same item.');
      return;
    }

    this.addProductToSlot(slotIndex, productData);

    // Visual feedback
    card.classList.add('is-selected', 'is-adding');
    setTimeout(() => {
      card.classList.remove('is-adding');

      // For preset bundles, auto-advance to next step
      if (this.bundleType === 'preset') {
        this.goToNextStep();
      }
    }, 800);

    // Scroll to top for mix & match so user sees updated slots
    if (this.bundleType === 'mix_and_match') {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  extractProductData(card) {
    return {
      id: card.dataset.productId,
      handle: card.dataset.productHandle,
      variantId: card.dataset.variantId,
      variantTitle: card.dataset.variantTitle || 'Default Title',
      title: card.dataset.productTitle,
      price: parseInt(card.dataset.variantPrice) || 0,
      comparePrice: parseInt(card.dataset.comparePrice) || parseInt(card.dataset.variantPrice) || 0,
      valueMetafield: parseInt(card.dataset.valueMetafield) || 0,
      image: card.dataset.productImage,
      cardElement: card
    };
  }

  addProductToSlot(slotIndex, productData) {
    this.selectedProducts[slotIndex] = productData;
    this.updateUI();
    this.trackEvent('add_to_bundle', productData);

    // Auto-open kit drawer when bundle is full
    if (this.getSelectedCount() >= this.maxItems) {
      this.openKitDrawer();
    }
  }

  removeProductFromSlot(slotIndex) {
    const product = this.selectedProducts[slotIndex];
    if (product) {
      // Remove selected state from card
      if (product.cardElement) {
        product.cardElement.classList.remove('is-selected');
      }

      delete this.selectedProducts[slotIndex];
      this.updateUI();
      this.trackEvent('remove_from_bundle', product);
    }
  }

  getNextAvailableSlotIndex() {
    const count = this.getSelectedCount();
    for (let i = 0; i < this.maxItems; i++) {
      if (!this.selectedProducts[i] && this.isSlotUnlocked(i, count)) {
        return i;
      }
    }
    return -1;
  }

  isSlotUnlocked(index, count) {
    if (count === undefined) count = this.getSelectedCount();
    return index < this.minItems || index < count + 1;
  }

  getSelectedCount() {
    return Object.keys(this.selectedProducts).length;
  }

  getVariantCount(variantId) {
    return Object.values(this.selectedProducts).filter(p => String(p.variantId) === String(variantId)).length;
  }

  handleSlotClick(slotIndex) {
    if (this.bundleType === 'preset') {
      // For preset bundles, clicking a slot navigates to that step
      this.trackStepTabClick(slotIndex, this.stepTabs && this.stepTabs[slotIndex], 'Slot Number');
      this.goToStep(slotIndex);
    } else {
      // Ignore clicks on locked slots
      if (!this.isSlotUnlocked(slotIndex)) return;

      // For mix and match, clicking removes the product
      const product = this.selectedProducts[slotIndex];
      if (product) {
        this.removeProductFromSlot(slotIndex);
      }
    }
  }

  // ==================== Step Navigation (Preset Bundles) ====================

  goToStep(stepIndex) {
    // For preset bundles with panels, use panel count; otherwise use steps array
    const maxSteps = this.stepPanels.length > 0 ? this.stepPanels.length : this.steps.length;
    if (stepIndex < 0 || stepIndex >= maxSteps) return;
    if (stepIndex === this.currentStep) return;

    this.currentStep = stepIndex;
    this.currentPage = 1;

    // For preset bundles with pre-rendered panels, switch panels
    if (this.bundleType === 'preset' && this.stepPanels.length > 0) {
      this.switchStepPanel(stepIndex);
    } else {
      // Load products for this step's collection via AJAX
      const step = this.steps[stepIndex];
      if (step && step.collectionHandle) {
        this.loadStepProducts(step.collectionHandle);
      }
    }

    this.updateStepTabs();
    this.updateSlots();
    this.updateStepInstruction();
  }

  switchStepPanel(stepIndex) {
    // Hide all panels and show the active one
    this.stepPanels.forEach((panel, index) => {
      panel.classList.toggle('is-active', index === stepIndex);
    });
  }

  selectTier(tierIndex) {
    if (tierIndex === this.selectedTierIndex) return;

    this.selectedTierIndex = tierIndex;

    // Update set buttons
    this.setBtns.forEach((btn, index) => {
      btn.classList.toggle('is-active', index === tierIndex);
    });

    // Update discount text visibility
    const discountEls = this.querySelectorAll('.flex-bundle__set-discount');
    discountEls.forEach(el => {
      const elTierIndex = parseInt(el.dataset.tierIndex);
      el.style.display = elTierIndex === tierIndex ? '' : 'none';
    });

    // Update visible slots based on tier size
    const tier = this.tiers[tierIndex];
    if (tier) {
      const tierSize = tier.size;
      this.slots.forEach((slot, index) => {
        slot.style.display = index < tierSize ? '' : 'none';
        // Also hide/show labels
        const label = slot.nextElementSibling;
        if (label && label.classList.contains('flex-bundle__slot-label')) {
          label.style.display = index < tierSize ? '' : 'none';
        }
      });

      // Reset if current step is beyond new tier size
      if (this.currentStep >= tierSize) {
        this.currentStep = 0;
        this.goToStep(0);
      }

      // Clear products that are beyond the new tier size
      Object.keys(this.selectedProducts).forEach(key => {
        if (parseInt(key) >= tierSize) {
          delete this.selectedProducts[key];
        }
      });
    }

    this.updateUI();
  }

  updateStepInstruction() {
    if (!this.stepNameEl || !this.steps[this.currentStep]) return;

    const stepName = this.steps[this.currentStep].name || '';
    this.stepNameEl.textContent = stepName.toLowerCase();
  }

  goToNextStep() {
    const nextStep = this.currentStep + 1;

    // Get max steps based on panels or steps array
    const totalSteps = this.stepPanels.length > 0 ? this.stepPanels.length : this.steps.length;

    // Tier size only caps step advance when the user picks the set size via
    // [js-set-btn] (mix-and-match flow). Preset bundles have no tier-selection
    // UI — tiers there are pricing/progress milestones only, so all defined
    // steps must remain reachable via auto-advance.
    let maxSteps = totalSteps;
    if (this.setBtns && this.setBtns.length > 0) {
      const tier = this.tiers[this.selectedTierIndex];
      if (tier && tier.size < totalSteps) maxSteps = tier.size;
    }

    if (nextStep < maxSteps) {
      // Go to next step
      this.goToStep(nextStep);
    } else {
      // All steps complete - update UI
      this.updateStepTabs();
      this.updateStepInstruction();
    }
  }

  async loadStepProducts(collectionHandle) {
    if (!collectionHandle || this.isLoading) return;

    this.isLoading = true;
    this.productGrid?.classList.add('flex-bundle__grid--loading');

    try {
      const url = `/collections/${collectionHandle}?view=flex-bundle-products&page=${this.currentPage}`;
      const response = await fetch(url);
      const html = await response.text();

      // Parse and update grid
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelector('[js-product-grid]');

      if (newProducts && this.productGrid) {
        this.productGrid.innerHTML = newProducts.innerHTML;
        this.initCardSwipers();
        this.initQuickAddModals();
        this.updateSelectedCards();
        this.updateCardDisabledStates();
      }

      // Update pagination
      const totalPages = doc.querySelector('[data-total-pages]');
      if (totalPages) {
        this.totalPages = parseInt(totalPages.dataset.totalPages) || 1;
        this.renderPagination();
      }
    } catch (error) {
      console.error('Error loading step products:', error);
    } finally {
      this.isLoading = false;
      this.productGrid?.classList.remove('flex-bundle__grid--loading');
    }
  }

  updateStepTabs() {
    this.stepTabs.forEach((tab, index) => {
      const isActive = index === this.currentStep;
      const isCompleted = this.selectedProducts[index] !== undefined;

      tab.classList.toggle('is-active', isActive);
      tab.classList.toggle('is-completed', isCompleted && !isActive);

      // Show/hide check mark
      const checkEl = tab.querySelector('[js-step-tab-check]');
      if (checkEl) {
        checkEl.style.display = isCompleted ? 'inline-flex' : 'none';
      }
    });
  }

  // ==================== Quickshop Modal ====================

  openQuickshop(card) {
    if (!card || !this.quickshopModal) return;

    const productData = this.extractProductData(card);
    this.quickshopProduct = productData;
    this.quickshopSelectedVariant = null;

    // Get variants from card data
    let variants = [];
    try {
      variants = JSON.parse(card.dataset.variantsJson || '[]');
    } catch (e) {
      console.error('Error parsing variants:', e);
    }

    // Populate modal
    const imageEl = this.quickshopModal.querySelector('[js-quickshop-image]');
    const titleEl = this.quickshopModal.querySelector('[js-quickshop-product-title]');
    const priceEl = this.quickshopModal.querySelector('[js-quickshop-price]');
    const comparePriceEl = this.quickshopModal.querySelector('[js-quickshop-compare-price]');
    const swatchesEl = this.quickshopModal.querySelector('[js-quickshop-swatches]');
    const selectedValueEl = this.quickshopModal.querySelector('[js-quickshop-selected-value]');
    const addBtn = this.quickshopModal.querySelector('[js-quickshop-add]');

    if (imageEl) {
      imageEl.innerHTML = `<img src="${productData.image}" alt="${productData.title}">`;
    }

    if (titleEl) {
      titleEl.textContent = productData.title;
    }

    if (priceEl) {
      priceEl.textContent = this.formatMoney(productData.price);
    }

    if (comparePriceEl) {
      if (productData.comparePrice > productData.price) {
        comparePriceEl.textContent = this.formatMoney(productData.comparePrice);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    if (selectedValueEl) {
      selectedValueEl.textContent = '--';
    }

    // Render variant swatches
    if (swatchesEl && variants.length > 0) {
      swatchesEl.innerHTML = variants.map(variant => {
        const isAvailable = variant.available;
        return `
          <label
            class="flex-bundle__quickshop-swatch-label ${!isAvailable ? 'is-unavailable' : ''}"
            data-variant-id="${variant.id}"
            data-variant-title="${variant.title}"
            data-variant-price="${variant.price}"
            data-variant-compare-price="${variant.compare_at_price || variant.price}"
            data-variant-image="${variant.featured_image?.src || productData.image}"
            data-swatch-image="${variant.swatch_image || ''}"
            data-swatch-color="${variant.swatch_color || ''}"
          >
          <img src="${variant.swatch_image}" alt="${variant.title}" width="28" height="28">
          </label>
        `;
      }).join('');

      // Add click listeners to swatches
      swatchesEl.querySelectorAll('.flex-bundle__quickshop-swatch-label').forEach(swatch => {
        swatch.addEventListener('click', () => {
          if (swatch.classList.contains('is-unavailable')) return;

          // Update selection
          swatchesEl.querySelectorAll('.flex-bundle__quickshop-swatch-label').forEach(s => {
            s.classList.remove('is-selected');
          });
          swatch.classList.add('is-selected');

          // Store selected variant
          this.quickshopSelectedVariant = {
            id: swatch.dataset.variantId,
            title: swatch.dataset.variantTitle,
            price: parseInt(swatch.dataset.variantPrice),
            comparePrice: parseInt(swatch.dataset.variantComparePrice),
            image: swatch.dataset.variantImage,
            swatchImage: swatch.dataset.swatchImage,
            swatchColor: swatch.dataset.swatchColor
          };

          // Update UI
          if (selectedValueEl) {
            selectedValueEl.textContent = this.quickshopSelectedVariant.title;
          }

          if (priceEl) {
            priceEl.textContent = this.formatMoney(this.quickshopSelectedVariant.price);
          }

          if (comparePriceEl) {
            if (this.quickshopSelectedVariant.comparePrice > this.quickshopSelectedVariant.price) {
              comparePriceEl.textContent = this.formatMoney(this.quickshopSelectedVariant.comparePrice);
              comparePriceEl.style.display = '';
            } else {
              comparePriceEl.style.display = 'none';
            }
          }

          if (imageEl && this.quickshopSelectedVariant.image) {
            imageEl.innerHTML = `<img src="${this.quickshopSelectedVariant.image}" alt="${productData.title}">`;
          }

          // Enable add button
          if (addBtn) {
            addBtn.disabled = false;
          }
        });
      });
    }

    // Disable add button until variant selected
    if (addBtn) {
      addBtn.disabled = true;
    }

    // Open modal
    this.quickshopModal.classList.add('is-open');
    this.quickshopModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('flex-bundle-modal-open');
  }

  closeQuickshop() {
    if (!this.quickshopModal) return;

    this.quickshopModal.classList.remove('is-open');
    this.quickshopModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('flex-bundle-modal-open');

    this.quickshopProduct = null;
    this.quickshopSelectedVariant = null;
  }

  handleQuickshopAdd() {
    if (!this.quickshopProduct || !this.quickshopSelectedVariant) return;

    const nextSlotIndex = this.getNextAvailableSlotIndex();
    if (nextSlotIndex === -1) {
      console.log('Bundle is full');
      this.closeQuickshop();
      return;
    }

    // Merge product data with selected variant
    const productData = {
      ...this.quickshopProduct,
      variantId: this.quickshopSelectedVariant.id,
      variantTitle: this.quickshopSelectedVariant.title,
      price: this.quickshopSelectedVariant.price,
      comparePrice: this.quickshopSelectedVariant.comparePrice,
      image: this.quickshopSelectedVariant.image || this.quickshopProduct.image,
      swatchImage: this.quickshopSelectedVariant.swatchImage,
      swatchColor: this.quickshopSelectedVariant.swatchColor
    };

    this.addProductToSlot(nextSlotIndex, productData);

    // Mark card as selected
    if (this.quickshopProduct.cardElement) {
      this.quickshopProduct.cardElement.classList.add('is-selected');
    }

    this.closeQuickshop();
  }

  // ==================== Kit Drawer ====================

  openKitDrawer() {
    if (!this.kitDrawer) return;

    this.populateKitDrawer();
    this.kitDrawer.classList.add('is-open');
    this.kitDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('flex-bundle-drawer-open');
  }

  closeKitDrawer() {
    if (!this.kitDrawer) return;

    this.kitDrawer.classList.remove('is-open');
    this.kitDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('flex-bundle-drawer-open');
  }

  populateKitDrawer() {
    if (!this.kitDrawerItems) return;

    const products = Object.entries(this.selectedProducts);

    if (products.length === 0) {
      this.kitDrawerItems.innerHTML = '<li class="flex-bundle__kit-drawer-empty">No items selected</li>';
      return;
    }

    // Fixed-price kits have no per-item price — show only the struck-through
    // standalone value so line items don't contradict the kit total
    const isFixedPrice = this.pricingType === 'fixed_price';

    this.kitDrawerItems.innerHTML = products.map(([slotIndex, product]) => {
      const hasVariant = product.variantTitle && product.variantTitle !== 'Default Title';
      const priceHtml = isFixedPrice
        ? `<s class="flex-bundle__kit-drawer-item-compare">${this.formatMoney(product.comparePrice || product.price)}</s>`
        : `${this.formatMoney(product.price)}
              ${product.comparePrice && product.comparePrice > product.price ? `<s class="flex-bundle__kit-drawer-item-compare">${this.formatMoney(product.comparePrice)}</s>` : ''}`;
      const swatchStyle = product.swatchImage
        ? `background: url(${product.swatchImage}) no-repeat center center; background-size: cover;`
        : product.swatchColor
          ? `background-color: ${product.swatchColor};`
          : `background-color: ${this.getSwatchColor(product.variantTitle)};`;

      return `
        <li class="flex-bundle__kit-drawer-item" data-slot-index="${slotIndex}">
          <img
            class="flex-bundle__kit-drawer-item-image"
            src="${product.image}"
            alt="${product.title}"
            width="80"
            height="80"
          >
          <div class="flex-bundle__kit-drawer-item-info">
            <h4 class="flex-bundle__kit-drawer-item-title">${product.title}</h4>
            ${hasVariant ? `
              <div class="flex-bundle__kit-drawer-item-variant">
                <span class="flex-bundle__kit-drawer-item-swatch" style="${swatchStyle}"></span>
                ${product.variantTitle}
              </div>
            ` : ''}
            <div class="flex-bundle__kit-drawer-item-price">
              ${priceHtml}
            </div>
            <div class="flex-bundle__kit-drawer-item-actions">
              <button type="button" class="flex-bundle__kit-drawer-item-action" data-action="remove" data-slot="${slotIndex}">Remove</button>
            </div>
          </div>
        </li>
      `;
    }).join('');

    // Add event listeners to action buttons
    this.kitDrawerItems.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = btn.dataset.action;
        const slotIndex = parseInt(btn.dataset.slot);

        if (action === 'remove') {
          this.removeProductFromSlot(slotIndex);
          this.populateKitDrawer();
          this.updateKitDrawerTotals();

          // Close drawer if no items left
          if (this.getSelectedCount() === 0) {
            this.closeKitDrawer();
          }
        } else if (action === 'edit') {
          // Close drawer and scroll to product in grid
          this.closeKitDrawer();
          const product = this.selectedProducts[slotIndex];
          if (product && product.cardElement) {
            product.cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Open quickshop if it has variants
            if (product.cardElement.dataset.hasVariants === 'true') {
              this.openQuickshop(product.cardElement);
            }
          }
        }
      });
    });

    this.updateKitDrawerTotals();
  }

  updateKitDrawerTotals() {
    const savings = this.calculateSavings();
    const total = this.calculateTotal();

    if (this.drawerSavingsAmount) {
      this.drawerSavingsAmount.textContent = this.formatMoney(savings);
    }

    if (this.drawerTotalDisplay) {
      this.drawerTotalDisplay.textContent = this.formatMoney(total);
    }

    if (this.drawerTotal) {
      this.drawerTotal.textContent = this.formatMoney(total);
    }

    if (this.drawerAddToCartBtn) {
      const count = this.getSelectedCount();
      this.drawerAddToCartBtn.disabled = count < this.minItems;
    }
  }

  calculateTotal() {
    const products = Object.values(this.selectedProducts);
    if (products.length === 0) return 0;

    let totalPrice = 0;
    products.forEach(product => {
      totalPrice += product.price;
    });

    // Apply tier discount
    const tier = this.getTierForItemCount(products.length);

    if (this.pricingType === 'fixed_price' && tier) {
      return this.getFixedTierPrice(tier);
    } else if (this.pricingType === 'percent_off' && tier) {
      const discountPercent = parseInt(tier.value) || 0;
      const discountMultiplier = 1 - (discountPercent / 100);
      return Math.round(totalPrice * discountMultiplier);
    }

    return totalPrice;
  }

  getSwatchColor(variantTitle) {
    // Try to map variant title to a color
    // This is a simple implementation - could be enhanced with actual color data
    const colorMap = {
      'fair': '#f5e6d3',
      'light': '#f0d9c1',
      'medium': '#d4a574',
      'tan': '#c4956a',
      'deep': '#8b5a2b',
      'dark': '#5c3a21',
      'porcelain': '#fdf5e6',
      'nude': '#e3c9b8',
      'pink': '#ffc0cb',
      'rose': '#ff007f',
      'berry': '#8e4585',
      'coral': '#ff7f50',
      'red': '#ff0000',
      'mauve': '#e0b0ff',
      'plum': '#8b008b'
    };

    const lowerTitle = variantTitle.toLowerCase();
    for (const [key, color] of Object.entries(colorMap)) {
      if (lowerTitle.includes(key)) {
        return color;
      }
    }

    // Default to a neutral color
    return '#d4a574';
  }

  // ==================== UI Updates ====================

  updateUI() {
    this.updateSlots();
    this.updateSavings();
    this.updateAddToCartButton();
    this.updateSelectedCards();
    this.updateViewKitButton();
    this.updateProgressBar();

    // Update step tabs and instruction for preset bundles
    if (this.bundleType === 'preset') {
      this.updateStepTabs();
      this.updateStepInstruction();
    }

    this.updateCardDisabledStates();
  }

  updateCardDisabledStates() {
    const count = this.getSelectedCount();
    const isFull = count >= this.maxItems;
    this.classList.toggle('is-bundle-full', isFull);

    // Disable/enable add-to-kit buttons and modal openers on product cards
    const grids = this.bundleType === 'preset' && this.productGrids.length > 0
      ? this.productGrids
      : this.productGrid ? [this.productGrid] : [];

    grids.forEach(grid => {
      grid.querySelectorAll('[js-product-card]').forEach(card => {
        const isSelected = card.classList.contains('is-selected');
        const shouldDisable = isFull && !isSelected;

        // Disable direct add-to-kit buttons
        const addBtn = card.querySelector('[js-add-to-kit]');
        if (addBtn) addBtn.disabled = shouldDisable;

        // Disable modal openers (SELECT SHADE buttons)
        const modalOpener = card.querySelector('modal-opener button');
        if (modalOpener) modalOpener.disabled = shouldDisable;
      });
    });
  }

  updateViewKitButton() {
    if (!this.viewKitBtn) return;

    const count = this.getSelectedCount();
    // Disable button until at least 1 item is selected
    this.viewKitBtn.disabled = count === 0;
  }

  updateProgressBar() {
    if (!this.progressBar || !this.progressFill || !this.progressMessage) return;

    const count = this.getSelectedCount();
    const fillPercent = Math.min((count / this.maxItems) * 100, 100);

    this.progressFill.style.width = `${fillPercent}%`;

    // Update tier marker active states
    this.progressBar.querySelectorAll('.flex-bundle__progress-tier-marker').forEach(marker => {
      const tierSize = parseInt(marker.dataset.tierSize);
      marker.classList.toggle('is-reached', count >= tierSize);
    });

    // Build progress message
    const currentTier = this.getTierForItemCount(count);
    const nextTier = this.getNextTier(count);

    if (nextTier) {
      const remaining = nextTier.size - count;
      const nextLabel = this.pricingType === 'percent_off' ? `${nextTier.value}% off` : this.formatMoney(parseInt(nextTier.value));
      this.progressMessage.innerHTML = `Add ${remaining} more to save <strong>${nextLabel}</strong>!`;
    } else if (currentTier) {
      const currentLabel = this.pricingType === 'percent_off' ? `${currentTier.value}% off` : this.formatMoney(parseInt(currentTier.value));
      this.progressMessage.innerHTML = `You're saving <strong>${currentLabel}</strong>! 🎉`;
    }
  }

  getNextTier(count) {
    for (const tier of this.tiers) {
      if (tier.size > count) {
        return tier;
      }
    }
    return null;
  }

  updateSlots() {
    const count = this.getSelectedCount();

    this.slots.forEach((slot, index) => {
      const product = this.selectedProducts[index];
      const numberEl = slot.querySelector('.flex-bundle__slot-number');
      const imageEl = slot.querySelector('[js-slot-image]');
      const removeBtn = slot.querySelector('[js-slot-remove]');
      const checkEl = slot.querySelector('[js-slot-check]');

      // Active state for preset bundles
      if (this.bundleType === 'preset') {
        slot.classList.toggle('active', index === this.currentStep);
      }

      // Locked state for mix-and-match slots beyond min
      const locked = !this.isSlotUnlocked(index, count);
      slot.classList.toggle('flex-bundle__slot--locked', locked);

      if (product) {
        slot.classList.add('filled');

        if (numberEl) numberEl.style.display = 'none';

        if (imageEl) {
          imageEl.src = product.image;
          imageEl.alt = product.title;
          imageEl.style.display = 'block';
        }

        if (checkEl) checkEl.style.display = 'flex';

        // Only show remove button for mix and match bundles
        if (removeBtn) {
          removeBtn.style.display = this.bundleType === 'preset' ? 'none' : 'flex';
        }
      } else {
        slot.classList.remove('filled');

        if (numberEl) numberEl.style.display = '';

        if (imageEl) {
          imageEl.src = '';
          imageEl.style.display = 'none';
        }

        if (checkEl) checkEl.style.display = 'none';

        if (removeBtn) removeBtn.style.display = 'none';
      }
    });
  }

  updateSavings() {
    if (!this.savingsDisplay) return;

    const savings = this.calculateSavings();
    this.savingsDisplay.textContent = this.formatMoney(savings);
  }

  calculateSavings() {
    const products = Object.values(this.selectedProducts);
    if (products.length === 0) return 0;

    let totalPrice = 0;
    let totalComparePrice = 0;
    products.forEach(product => {
      totalPrice += product.price;
      totalComparePrice += product.comparePrice || product.price;
    });

    // Calculate savings vs compare-at price (MSRP) after bundle discount
    const tier = this.getTierForItemCount(products.length);

    let finalPrice = totalPrice;
    if (this.pricingType === 'fixed_price' && tier) {
      finalPrice = this.getFixedTierPrice(tier);
    } else if (this.pricingType === 'percent_off' && tier) {
      const discountPercent = parseInt(tier.value) || 0;
      finalPrice = totalPrice - Math.round(totalPrice * (discountPercent / 100));
    }

    return Math.max(0, totalComparePrice - finalPrice);
  }

  // Fixed-price full kits are charged at the parent variant's price (Parent
  // Price mode), so display the parent's market catalog price when available —
  // it's the source of truth for market-specific pricing (£50 GB / €67 FR).
  // Fallback for partial tiers / missing data: tier values are authored in
  // shop-currency cents, converted to the active presentment currency.
  getFixedTierPrice(tier) {
    if (this.bundlePrice && tier.size === this.maxItems) {
      return this.bundlePrice;
    }
  }

  getTierForItemCount(count) {
    // Find the tier that matches the current item count
    // Tiers are sorted by size, find the matching or closest lower tier
    let matchingTier = null;

    for (const tier of this.tiers) {
      if (tier.size <= count) {
        matchingTier = tier;
      }
    }

    return matchingTier;
  }

  updateAddToCartButton() {
    if (!this.addToCartBtn) return;

    const count = this.getSelectedCount();

    // Enable button when minimum items are selected
    this.addToCartBtn.disabled = count < this.minItems;

    // Update button text
    if (count < this.minItems) {
      this.addToCartBtn.textContent = `Select ${this.minItems - count} more item${this.minItems - count > 1 ? 's' : ''}`;
    } else {
      this.addToCartBtn.textContent = this.addToCartText;
    }
  }

  updateSelectedCards() {
    // Update which cards show as selected
    const selectedIds = new Set(
      Object.values(this.selectedProducts).map(p => p.id)
    );

    // For preset bundles, update cards across all grids
    const gridsToUpdate = this.bundleType === 'preset' && this.productGrids.length > 0
      ? this.productGrids
      : this.productGrid ? [this.productGrid] : [];

    gridsToUpdate.forEach(grid => {
      grid.querySelectorAll('[js-product-card]').forEach(card => {
        const isSelected = selectedIds.has(card.dataset.productId);
        card.classList.toggle('is-selected', isSelected);
      });
    });
  }

  // ==================== Pagination ====================

  goToPage(page) {
    if (page < 1 || page > this.totalPages || this.isLoading) return;

    this.currentPage = page;
    this.loadProducts();
  }

  async loadProducts() {
    if (!this.collectionHandle) return;

    this.isLoading = true;
    this.productGrid?.classList.add('flex-bundle__grid--loading');

    try {
      const url = `/collections/${this.collectionHandle}?view=flex-bundle-products&page=${this.currentPage}`;
      const response = await fetch(url);
      const html = await response.text();

      // Parse and update grid
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelector('[js-product-grid]');

      if (newProducts && this.productGrid) {
        this.productGrid.innerHTML = newProducts.innerHTML;
        this.initCardSwipers();
        this.initQuickAddModals();
        this.updateSelectedCards();
        this.updateCardDisabledStates();

        // Scroll grid into view
        this.productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Update pagination
      const totalPages = doc.querySelector('[data-total-pages]');
      if (totalPages) {
        this.totalPages = parseInt(totalPages.dataset.totalPages) || 1;
        this.renderPagination();
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      this.isLoading = false;
      this.productGrid?.classList.remove('flex-bundle__grid--loading');
    }
  }

  renderPagination() {
    if (!this.pagination || this.totalPages <= 1) {
      if (this.pagination) this.pagination.style.display = 'none';
      return;
    }

    this.pagination.style.display = 'flex';

    let html = '';

    // Previous button
    html += `
      <button class="flex-bundle__pagination-btn flex-bundle__pagination-arrow" data-page="${this.currentPage - 1}" ${this.currentPage === 1 ? 'disabled' : ''}>
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="2"/></svg>
      </button>
    `;

    // Page numbers
    for (let i = 1; i <= this.totalPages; i++) {
      if (
        i === 1 ||
        i === this.totalPages ||
        (i >= this.currentPage - 2 && i <= this.currentPage + 2)
      ) {
        html += `
          <button class="flex-bundle__pagination-btn ${i === this.currentPage ? 'is-active' : ''}" data-page="${i}">
            ${i}
          </button>
        `;
      } else if (i === this.currentPage - 3 || i === this.currentPage + 3) {
        html += `<span class="flex-bundle__pagination-ellipsis">...</span>`;
      }
    }

    // Next button
    html += `
      <button class="flex-bundle__pagination-btn flex-bundle__pagination-arrow" data-page="${this.currentPage + 1}" ${this.currentPage === this.totalPages ? 'disabled' : ''}>
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="2"/></svg>
      </button>
    `;

    this.pagination.innerHTML = html;
  }

  // ==================== Cart Integration (Flex Bundles App) ====================

  async addBundleToCart() {
    const products = Object.values(this.selectedProducts);

    if (products.length === 0) {
      console.log('No products selected');
      return;
    }

    if (!this.parentVariantId) {
      console.error('No parent variant ID configured');
      return;
    }

    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Adding...';

    // Calculate discounted price per item based on tier
    const tier = this.getTierForItemCount(products.length);
    let discountMultiplier = 1;

    if (this.pricingType === 'percent_off' && tier) {
      const discountPercent = parseInt(tier.value) || 0;
      discountMultiplier = 1 - (discountPercent / 100);
    }

    // Build components array for Flex Bundles
    // Fixed-price bundles omit price — the parent variant's price is the kit price
    const components = products.map(product => {
      if (this.pricingType === 'fixed_price') {
        return {
          id: parseInt(product.variantId),
          quantity: 1,
        };
      }

      let discountedPrice = product.price / 100; // Convert cents to dollars

      if (this.pricingType === 'percent_off' && tier) {
        discountedPrice = Math.round(product.price * discountMultiplier) / 100;
      }

      return {
        id: parseInt(product.variantId),
        quantity: 1,
        price: discountedPrice,
      };
    });

    // Build line item properties for display
    const itemProperties = {};
    products.forEach((product, index) => {
      const displayName = product.variantTitle && product.variantTitle !== 'Default Title'
        ? `${product.title} - ${product.variantTitle}`
        : product.title;
      itemProperties[`Item ${index + 1}`] = displayName;
    });

    // Get cart drawer sections to include in the add-to-cart request
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map(s => s.id)
      : [];

    const formData = {
      items: [{
        id: parseInt(this.parentVariantId),
        quantity: 1,
        properties: {
          "_components": JSON.stringify(components),
          "_settings": JSON.stringify({ title: this.cartTitle || this.bundleTitle }),
          "_title": this.cartTitle || this.bundleTitle,
          ...itemProperties
        }
      }],
      sections: sectionsToRender,
      sections_url: window.location.pathname
    };

    // Log payload for debugging
    console.log('=== Flex Bundles Cart Payload ===');
    console.log('Parent Variant ID:', this.parentVariantId);
    console.log('Components:', components);
    console.log('_components (stringified):', JSON.stringify(components));
    console.log('Full formData:', JSON.stringify(formData, null, 2));
    console.log('=================================');

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        this.trackEvent('add_bundle_to_cart', { products, data });

        // Refresh cart drawer using the same approach as the theme's onCartUpdate
        if (cartDrawer) {
          const sectionResponse = await fetch(window.Shopify.routes.root + 'cart?section_id=cart-drawer');
          const sectionHtml = await sectionResponse.text();
          const html = new DOMParser().parseFromString(sectionHtml, 'text/html');

          // Replace cart drawer items and footer (same as cart.js onCartUpdate)
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          // Update cart icon bubble count
          const bubbleResponse = await fetch(window.Shopify.routes.root + 'cart?section_id=cart-icon-bubble');
          const bubbleHtml = await bubbleResponse.text();
          const bubbleDom = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const targetBubble = document.getElementById('cart-icon-bubble');
          const sourceBubble = bubbleDom.querySelector('#cart-icon-bubble');
          if (targetBubble && sourceBubble) {
            targetBubble.replaceWith(sourceBubble);
          }

          // Remove is-empty class if needed
          cartDrawer.classList.remove('is-empty');
          const drawerInner = cartDrawer.querySelector('.drawer__inner');
          if (drawerInner) drawerInner.classList.remove('is-empty');

          cartDrawer.open();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }

        // Reset bundle
        this.resetBundle();
      } else {
        const error = await response.json();
        console.error('Error adding to cart:', error);
        alert('Error adding bundle to cart. Please try again.');
      }
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      alert('Error adding bundle to cart. Please try again.');
    } finally {
      this.updateAddToCartButton();
    }
  }

  resetBundle() {
    // Clear all selected products
    Object.keys(this.selectedProducts).forEach(key => {
      const product = this.selectedProducts[key];
      if (product.cardElement) {
        product.cardElement.classList.remove('is-selected');
      }
    });

    this.selectedProducts = {};
    this.updateUI();
  }

  // ==================== Utilities ====================

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
    const amount = cents / 100;
    if (this.currencyFormatter) {
      return this.currencyFormatter.format(amount);
    }
    return `$${amount.toFixed(2)}`;
  }

  trackEvent(eventName, data = {}) {
    // Google Analytics / Data Layer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'flex_bundle',
        action: eventName,
        bundle_type: this.bundleType,
        pricing_type: this.pricingType,
        ...data
      });
    }
  }

  // ==================== Bundle Builder Click Tracking ====================

  getClickText(el) {
    if (!el || !el.textContent) return undefined;
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    return text || undefined;
  }

  // Step/category name for preset bundles; undefined for mix & match
  getStepName(stepIndex) {
    if (this.bundleType !== 'preset') return undefined;
    const index = stepIndex !== undefined ? stepIndex : this.currentStep;
    const tab = this.stepTabs && this.stepTabs[index];
    const titleEl = tab && tab.querySelector('.flex-bundle__step-tab-title');
    if (titleEl) return this.getClickText(titleEl);
    const step = this.steps[index];
    return step && step.name ? step.name : undefined;
  }

  // Slot the next product-card click would fill
  getTargetSlotIndex() {
    return this.bundleType === 'preset' ? this.currentStep : this.getNextAvailableSlotIndex();
  }

  trackProductCardClick(button, card) {
    const slotIndex = this.getTargetSlotIndex();
    this.trackBundleBuilderClick({
      clickType: 'Product Card',
      productIndex: slotIndex === -1 ? undefined : slotIndex + 1,
      linkText: this.getClickText(button),
      linkPath: card && card.dataset.productUrl ? card.dataset.productUrl : undefined
    });
  }

  // clickType: 'Selection Tab' for the labeled step tabs, 'Slot Number' for the numbered slot circles
  trackStepTabClick(stepIndex, tabEl, clickType) {
    const titleEl = tabEl && tabEl.querySelector('.flex-bundle__step-tab-title');
    const stepName = titleEl ? this.getClickText(titleEl) : this.getStepName(stepIndex);
    this.trackBundleBuilderClick({
      clickType: clickType || 'Selection Tab',
      productIndex: stepIndex + 1,
      activeState: stepName,
      linkText: stepName
    });
  }

  trackBundleBuilderClick({ clickType, productIndex, activeState, linkText, linkPath }) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'bundle_builder',
      click_type: clickType,
      product_index: productIndex,
      active_state: activeState !== undefined ? activeState : this.getStepName(),
      link_text: linkText,
      link_path: linkPath,
      component_name: this.bundleTitle,
      component_id: this.sectionId
    });
  }
}

// Register the custom element
customElements.define('flex-bundle-builder', FlexBundleBuilder);